<script setup>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

import { ref, nextTick } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useDetectDevice } from '@/composables/device'
import NavigateBack from '@/components/shared/NavigateBack.vue'

defineProps({
  modelValue: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'crop'])

const { t } = getI18n()
const { isMobile } = useDetectDevice()

const imageRef = ref(null)
let cropper = null

const onShow = async() => {
  await nextTick()
  cropper = new Cropper(imageRef.value, {
    viewMode: 1,
    aspectRatio: 1,
    autoCropArea: 1,
    background: false,
    responsive: true,
  })
}

const toCircularDataUrl = (canvas) => {
  const round = document.createElement('canvas')
  round.width = canvas.width
  round.height = canvas.height
  const ctx = round.getContext('2d')
  ctx.beginPath()
  ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()
  ctx.drawImage(canvas, 0, 0)
  return round.toDataURL('image/png')
}

const onHide = () => {
  cropper?.destroy()
  cropper = null
}

const close = () => emit('update:modelValue', null)

const onConfirm = () => {
  emit('crop', toCircularDataUrl(cropper.getCroppedCanvas()))
  close()
}
</script>
<template>
  <q-dialog
    :model-value="modelValue !== null"
    :maximized="isMobile"
    persistent
    class="dialog-xs"
    @show="onShow"
    @hide="onHide"
  >
    <q-card class="dialog-card-flex">
      <navigate-back :text="t('common.crop')" @cancel="close" />
      <q-card-section class="scroll col q-pa-sm">
        <div class="crop-area">
          <img ref="imageRef" :src="modelValue">
        </div>
      </q-card-section>
      <q-card-actions align="between" class="q-pa-md">
        <div>
          <q-btn
            flat
            round
            icon="mdi-rotate-left"
            @click="cropper?.rotate(-90)"
          />
          <q-btn
            flat
            round
            icon="mdi-rotate-right"
            @click="cropper?.rotate(90)"
          />
          <q-btn
            flat
            round
            icon="mdi-magnify-plus-outline"
            @click="cropper?.zoom(0.1)"
          />
          <q-btn
            flat
            round
            icon="mdi-magnify-minus-outline"
            @click="cropper?.zoom(-0.1)"
          />
          <q-btn
            flat
            round
            icon="mdi-restore"
            @click="cropper?.reset()"
          />
        </div>
        <q-btn color="primary" :label="t('common.confirm')" @click="onConfirm" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style scoped>
.crop-area {
  max-height: 60vh;
}
.crop-area img {
  display: block;
  max-width: 100%;
  max-height: 60vh;
}
.crop-area :deep(.cropper-view-box),
.crop-area :deep(.cropper-face) {
  border-radius: 50%;
}
</style>
